import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useGameStore } from '@/store/gameStore'
import { FACILITY_VISUALS, getFacilityAsset } from '@/config/campusConfig'
import { constructionRevealCopy } from '@/config/constructionConfig'
import { backdropVariants, modalVariants, SPRING_REVEAL, useMotionVariants } from '../motion/motionTokens'
import { useTopModal } from '../modals/useTopModal'
import { AngledButton } from './broadcast'
import { ModalCloseButton } from './ModalCloseButton'
import { CheckCircle2, HardHat } from 'lucide-react'

// The "works finished" transition — same celebratory language as the level-up
// modal. Driven by the queue the ConstructionSystem fills when a job ends.
export function ConstructionCompleteModal() {
  const job = useGameStore(s => s.pendingConstructionReveals[0] ?? null)
  const dismiss = useGameStore(s => s.dismissConstructionReveal)
  // Shown only when it's the top of the modal queue — so it never stacks on a
  // level-up, story beat, weekly report, etc. (see useTopModal).
  const top = useTopModal()
  const navigate = useNavigate()
  const modal = useMotionVariants(modalVariants)
  const backdrop = useMotionVariants(backdropVariants)
  const [imgOk, setImgOk] = useState(true)

  const visual = job ? FACILITY_VISUALS.find(v => v.id === job.visualId) : null
  const asset = job && visual ? getFacilityAsset(visual, job.tier) : null
  const copy = job ? constructionRevealCopy(job) : null

  useEffect(() => { setImgOk(true) }, [asset?.image])

  const close = () => dismiss()
  const goManage = () => {
    dismiss()
    navigate('/facilities')
  }

  return (
    <AnimatePresence>
      {job && copy && top === 'construction' && (
        <>
          <motion.div
            variants={backdrop}
            initial="initial"
            animate="enter"
            exit="exit"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200 }}
          />

          <motion.div
            variants={modal}
            initial="initial"
            animate="enter"
            exit="exit"
            className="pointer-events-auto"
            style={{
              position: 'fixed', top: '50%', left: '50%', x: '-50%', y: '-50%',
              zIndex: 201,
              background: 'var(--bg-surface)',
              border: '1px solid color-mix(in srgb, var(--gold) 35%, transparent)',
              borderRadius: '16px',
              overflow: 'hidden auto',
              maxHeight: '90dvh',
              width: 'min(440px, 92vw)',
              textAlign: 'center',
              boxShadow: 'var(--shadow-lg), inset 0 3px 0 0 var(--gold)',
            }}
          >
            <ModalCloseButton onClick={close} />
            <motion.div
              initial={{ scale: 1.12, filter: 'brightness(1.5)' }}
              animate={{ scale: 1, filter: 'brightness(1)' }}
              transition={{ ...SPRING_REVEAL, delay: 0.1 }}
              style={{ position: 'relative', aspectRatio: '16 / 8', background: 'var(--bg-primary)' }}
            >
              {asset && imgOk ? (
                <img
                  src={asset.image}
                  alt={job.label}
                  onError={() => setImgOk(false)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: 'var(--gold)',
                }}>
                  <HardHat size={56} />
                </div>
              )}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(180deg, transparent 40%, rgba(13,21,32,0.92) 100%)',
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                padding: '0.9rem 1.25rem', textAlign: 'left',
              }}>
                <div style={{
                  fontSize: '0.72rem', color: 'var(--gold)',
                  letterSpacing: '2.5px', textTransform: 'uppercase', fontWeight: 700,
                }}>
                  {copy.kicker}
                </div>
                <div style={{
                  fontSize: '1.5rem', fontWeight: 800, color: '#fff',
                  fontFamily: 'var(--font-titles)', textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                }}>
                  {job.label}
                </div>
              </div>
            </motion.div>

            <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                  background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)',
                  borderRadius: '8px', padding: '0.6rem', marginBottom: '1.1rem',
                  color: 'var(--success)', fontWeight: 600, fontSize: '0.85rem',
                }}
              >
                <CheckCircle2 size={16} />
                {asset?.caption ?? 'La obra ha finalizado y ya forma parte de tu club.'}
              </motion.div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <AngledButton tone="neutral" onClick={close}>Cerrar</AngledButton>
                <AngledButton tone="gold" block onClick={goManage}>{copy.cta}</AngledButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
